import { AttachmentCaption } from '@/components/shared/CaptionBox/AttachmentCaption';
import { useAttachmentCaption } from '@/hooks/useAttachmentCaptions';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
import { useEffect } from 'react';
import { usePoiAttachmentCaption } from '@/hooks/usePoiAttachmentCaption';
import { Attachment } from '@/components/shared/AttachmentDisplay/type';
import { PointOfInterest } from '@/__generated__/graphql';
import { poiState } from '@/components/Analysis/modules/pointOfInterest';
import { useSetAtom } from 'jotai';
import { CaptionTextFieldLength } from './styles';
import { useReviewIn3D } from '../../hooks/useReviewIn3D';

type Props = {
  selectedAttachment: Attachment;
  selectedPointOfInterest: PointOfInterest;
  location?: string;
};
export const PointOfInterestCaption = ({
  selectedAttachment,
  selectedPointOfInterest,
  location = '',
}: Props) => {
  const {
    isEditing,
    setIsEditing,
    attachmentCaption,
    setAttachmentCaption,
    handleCaption,
    captionValidator,
  } = useAttachmentCaption();

  let currentViewpoint: string | undefined;
  if (selectedAttachment.type === 'viewpoint') {
    currentViewpoint = selectedAttachment.name;
  }
  const { updatePoiAttachmentCaption } = usePoiAttachmentCaption();
  const { showSnackBar } = useSnackBarMessage({});
  const { handleViewIn3D } = useReviewIn3D({
    pointOfInterest: selectedPointOfInterest,
    currentViewpoint: currentViewpoint,
  });
  const setAllPOI = useSetAtom(poiState.structurePois);
  useEffect(() => {
    setAttachmentCaption(selectedAttachment.caption ?? selectedAttachment.fileName);
  }, [selectedAttachment, setAttachmentCaption]);

  const handelAttachmentCaption = async () => {
    try {
      setIsEditing(false);
      const isValid = captionValidator(selectedAttachment, selectedPointOfInterest.id);
      if (!isValid || !selectedAttachment?.id || !attachmentCaption) {
        if (isValid) {
          showSnackBar('Missing or invalid data for caption update', 'error');
        }
        return;
      }
      await updatePoiAttachmentCaption(
        selectedPointOfInterest.id,
        selectedAttachment.uuid ?? '',
        selectedAttachment.id,
        attachmentCaption
      );
      setAllPOI((prevPois) =>
        prevPois.map((poi) => {
          if (poi.id !== selectedPointOfInterest.id) return poi;
          return {
            ...poi,
            files: poi.files?.map((file) => ({
              ...file,
              caption: file.id === selectedAttachment.id ? attachmentCaption : file.caption,
            })),
          };
        })
      );

      showSnackBar('Caption added successfully', 'success');
    } catch (error) {
      showSnackBar('Failed to update image caption', 'error');
    }
  };
  return (
    <AttachmentCaption
      isEditing={isEditing}
      collectionName="poi"
      attachmentCaption={attachmentCaption}
      setAttachmentCaption={setAttachmentCaption}
      handleAttachmentCaption={handelAttachmentCaption}
      setIsEditing={setIsEditing}
      handleCaption={handleCaption}
      selectedAttachment={selectedAttachment}
      sx={CaptionTextFieldLength}
      isViewPoint={!!currentViewpoint}
      handleViewIn3D={handleViewIn3D}
      location={location}
    />
  );
};
